import { useState } from "react";
import {
  MapPin,
  Users,
  Maximize2,
  DollarSign,
  Sparkles,
  ChevronRight,
  Coins,
  Info,
  RefreshCw,
  Clock,
} from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from "@/utils/format/format-date";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { RankedSpace, SpaceStaleStatus } from "../types";
import { ScoreDetailsModal } from "@/features/vision-360/components/score-details-modal";
import { translateCriterion } from "@/features/vision-360/utils/translateCriterion";

function formatScore(score: number): string {
  if (Number.isInteger(score)) return `${score}`;
  const fixed = score.toFixed(2);
  return fixed.replace(/0+$/, "").replace(/\.$/, "");
}

interface SpaceCardProps {
  space: RankedSpace;
  selected: boolean;
  active: boolean;
  onToggleSelect: () => void;
  onActivate: () => void;
  onViewDetails: () => void;
  onScoreClick?: () => void;
  onDiffClick?: () => void;
  hidePrice?: boolean;
  staleStatus?: SpaceStaleStatus;
  previousData?: Pick<
    RankedSpace,
    "pricePerWorkstation" | "monthlyPrice" | "capacity"
  >;
}

export function SpaceCard({
  space,
  selected,
  active,
  onToggleSelect,
  onActivate,
  onViewDetails,
  onScoreClick,
  onDiffClick,
  hidePrice,
  staleStatus,
  previousData,
}: SpaceCardProps) {
  const [scoreOpen, setScoreOpen] = useState(false);
  const isVipOffice =
    space.source === "vip-office" && space.origin === "integration";
  const displayCredits =
    space.credits != null && space.credits > 0
      ? space.credits
      : isVipOffice && space.monthlyPrice
        ? Math.ceil(space.monthlyPrice / 80)
        : null;
  const [scoreDetailsModalOpen, setScoreDetailsModalOpen] = useState(false);
  const hasScoreDetails =
    (space.detailedScoreAnalysis?.length ?? 0) > 0 || space.scoreDetails;
  const hasPitchOrReasons =
    Boolean(space.salesPitch) || (space.matchReasons?.length ?? 0) > 0;
  const isHighScore = space.score >= 80;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      className="rounded-xl border p-3 transition-all cursor-pointer"
      style={
        active
          ? {
              borderColor: "#FFD900",
              background: "rgba(255,217,0,0.06)",
              boxShadow: "0 0 0 1px rgba(255,217,0,0.4)",
            }
          : {
              borderColor: "#E8E6E3",
              background: "white",
            }
      }
    >
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span className="font-semibold text-sm text-gray-900 truncate">
            {space.roomName}
          </span>
          {space.availabilityDate && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full shrink-0 cursor-default"
                    style={{ background: "#FEF3C7", color: "#92400E" }}
                  >
                    <Clock className="w-3 h-3" />
                    Disponível em {formatDate(space.availabilityDate)}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-64 text-xs bg-gray-800 text-white">
                  A data pode mudar caso o contrato atual seja renovado.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {onDiffClick && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDiffClick();
                  }}
                  className="shrink-0 transition-opacity hover:opacity-70"
                >
                  <RefreshCw
                    className="w-3.5 h-3.5"
                    style={{ color: "#F59E0B" }}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Dados atualizados</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {space.addedById ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 cursor-default">
                  <Info className="w-3.5 h-3.5" style={{ color: "#78736D" }} />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Sala adicionada pelo ID</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : hasPitchOrReasons ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onScoreClick?.();
                setScoreOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 transition-opacity hover:opacity-75"
              style={
                isHighScore
                  ? { background: "#FFF8D6", color: "#92700C" }
                  : { background: "#F0EEEB", color: "#78736D" }
              }
            >
              {formatScore(space.score)}%
              {isHighScore && <Sparkles className="w-3 h-3" />}
            </button>
            <Dialog open={scoreOpen} onOpenChange={setScoreOpen}>
              <DialogContent
                className="max-w-lg p-0 overflow-hidden gap-0"
                showCloseButton={false}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="px-5 pt-5 pb-4"
                  style={{ background: "#1A1814" }}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <DialogTitle
                      className="text-sm font-semibold leading-snug"
                      style={{ color: "white" }}
                    >
                      {space.roomName} - #{space.searchId}
                    </DialogTitle>
                    <span
                      className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
                      style={
                        isHighScore
                          ? { background: "#FFD900", color: "#1A1814" }
                          : {
                              background: "rgba(255,255,255,0.15)",
                              color: "rgba(255,255,255,0.7)",
                            }
                      }
                    >
                      {formatScore(space.score)}%
                      {isHighScore && <Sparkles className="w-3 h-3" />}
                    </span>
                  </div>
                  {space.spaceName && (
                    <p
                      className="text-xs"
                      style={{ color: "rgba(255,255,255,0.45)" }}
                    >
                      {space.spaceName}
                    </p>
                  )}
                </div>
                <div
                  className="px-5 py-4 space-y-3"
                  style={{ background: "#FDFBF7" }}
                >
                  {space.salesPitch && (
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "#43403D" }}
                    >
                      {space.salesPitch}
                    </p>
                  )}
                  {space.matchReasons && space.matchReasons.length > 0 && (
                    <ul className="space-y-2">
                      {space.matchReasons.map((reason, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span
                            className="text-sm"
                            style={{ color: "#43403D" }}
                          >
                            {reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : hasScoreDetails ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setScoreDetailsModalOpen(true);
              }}
              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 transition-opacity hover:opacity-75"
              style={
                isHighScore
                  ? { background: "#FFF8D6", color: "#92700C" }
                  : { background: "#F0EEEB", color: "#78736D" }
              }
            >
              {formatScore(space.score)}%
              {isHighScore && <Sparkles className="w-3 h-3" />}
            </button>
            {scoreDetailsModalOpen && (
              <ScoreDetailsModal
                scoreDetails={{
                  scoreDetails: space.detailedScoreAnalysis ?? null,
                  details: space.scoreDetails ?? null,
                }}
                match={space.score}
                handleClose={() => setScoreDetailsModalOpen(false)}
              />
            )}
          </>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
            style={
              isHighScore
                ? { background: "#FFF8D6", color: "#92700C" }
                : { background: "#F0EEEB", color: "#78736D" }
            }
          >
            {formatScore(space.score)}%
            {isHighScore && <Sparkles className="w-3 h-3" />}
          </span>
        )}
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect()}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0"
        />
      </div>

      {space.inToleranceZone && (
        <div className="mt-1 flex items-center gap-1">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
            style={{
              background: "rgba(245,158,11,0.12)",
              color: "#D97706",
              border: "1px solid rgba(245,158,11,0.25)",
            }}
          >
            ⚠ Na tolerância
          </span>
          {space.scoreBreakdown
            ?.filter((c) => c.inTolerance)
            .map((c) => (
              <span
                key={c.label}
                className="text-[10px] px-1.5 py-0.5 rounded-full"
                style={{
                  background: "#F5F5F5",
                  color: "#78736D",
                }}
              >
                {translateCriterion(c.label)}
              </span>
            ))}
        </div>
      )}

      {staleStatus === "unavailable" && (
        <div className="mt-1.5">
          <span
            className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ background: "#FEF3C7", color: "#92400E" }}
          >
            Sala desatualizada
          </span>
        </div>
      )}
      {staleStatus === "data-changed" && (
        <div className="mt-1.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full cursor-default"
                  style={{ background: "#DBEAFE", color: "#1E40AF" }}
                >
                  Dados atualizados
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-56 space-y-1 text-xs">
                {previousData?.monthlyPrice !== undefined &&
                  previousData.monthlyPrice !== space.monthlyPrice && (
                    <p>
                      Preço: R${" "}
                      {previousData.monthlyPrice?.toLocaleString("pt-BR") ??
                        "—"}{" "}
                      → R$ {space.monthlyPrice?.toLocaleString("pt-BR") ?? "—"}
                    </p>
                  )}
                {previousData?.capacity !== undefined &&
                  previousData.capacity !== space.capacity && (
                    <p>
                      Capacidade: {previousData.capacity ?? "—"} →{" "}
                      {space.capacity ?? "—"}
                    </p>
                  )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      <div className="mt-1">
        <p className="text-xs text-gray-400">{space.spaceName}</p>
      </div>

      <div className="mt-1 flex items-center gap-1">
        <MapPin className="w-3 h-3 shrink-0" style={{ color: "#9E9A94" }} />
        <span className="text-xs truncate" style={{ color: "#78736D" }}>
          {space.address}
        </span>
      </div>

      <TooltipProvider>
        <div className="mt-1.5 flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="inline-flex items-center gap-1 text-xs"
                style={{ color: "#78736D" }}
              >
                <Users className="w-3 h-3" />
                {space.capacity != null && space.capacity > 0
                  ? space.capacity
                  : "Sob consulta"}
              </span>
            </TooltipTrigger>
            <TooltipContent>Capacidade</TooltipContent>
          </Tooltip>
          <span
            className="inline-flex items-center gap-1 text-xs"
            style={{ color: "#78736D" }}
          >
            <Maximize2 className="w-3 h-3" />
            {space.sizeM2 != null && space.sizeM2 > 0
              ? `${space.sizeM2} m²`
              : "Sob consulta"}
          </span>
          {!hidePrice && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center gap-1 text-xs"
                  style={{ color: "#78736D" }}
                >
                  <DollarSign className="w-3 h-3" />
                  {space.monthlyPrice != null && space.monthlyPrice > 0
                    ? `${isVipOffice ? "~" : ""}R$ ${space.monthlyPrice.toLocaleString("pt-BR")}/mês`
                    : "Sob consulta"}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isVipOffice
                  ? "Preço médio de referência · valor efetivo sob consulta"
                  : "Preço mensal"}
              </TooltipContent>
            </Tooltip>
          )}
          {!hidePrice && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center gap-1 text-xs"
                  style={{ color: "#78736D" }}
                >
                  <Coins className="w-3 h-3" />
                  {displayCredits != null && displayCredits > 0
                    ? `${isVipOffice ? "~" : ""}${displayCredits.toLocaleString("pt-BR")}`
                    : "Sob consulta"}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isVipOffice
                  ? "Créditos estimados com base no preço de referência"
                  : "Créditos"}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>

      <div className="ml-6 mt-2 flex justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails();
          }}
          className="inline-flex items-center gap-0.5 text-xs font-medium transition-colors hover:opacity-80"
          style={{ color: "#43403D" }}
        >
          Ver detalhes
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
